import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function ProtectedRoute({ children }) {
  const { user, loading, authRejected, login, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--aw-bg)" }}
      >
        <div
          className="flex items-center gap-3"
          style={{ color: "var(--aw-muted)" }}
        >
          <div
            className="rounded-full spin"
            style={{
              width: 16,
              height: 16,
              border: "1px solid var(--aw-border)",
              borderTopColor: "var(--aw-ink)",
            }}
          />
          <span
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            }}
          >
            Loading
          </span>
        </div>
      </div>
    );
  }

  // The backend rejected the user's Auth0 token (most often: stale session,
  // audience mismatch, or JWKS fetch failure). Show a clear "your session
  // expired" screen rather than dumping them onto a half-broken dashboard.
  if (authRejected) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "var(--aw-bg)" }}
        data-testid="auth-rejected-screen"
      >
        <div
          className="card-aw card-aw-lg"
          style={{ maxWidth: 460, textAlign: "center" }}
        >
          <div className="eyebrow" style={{ justifyContent: "center" }}>
            Session expired
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 28,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            Your sign-in has timed out.
          </h1>
          <p
            className="mt-3 text-sm"
            style={{ color: "var(--aw-muted)", lineHeight: 1.55 }}
          >
            We couldn't verify your access token. This usually means your
            session has expired or you signed in from a different environment.
            Sign in again to continue where you left off.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              onClick={() => login(location.pathname + location.search)}
              data-testid="auth-rejected-relogin"
              className="btn-aw btn-aw-primary"
            >
              Sign in again <span className="arrow">→</span>
            </button>
            <button
              onClick={logout}
              data-testid="auth-rejected-logout"
              className="btn-aw btn-aw-ghost btn-aw-sm"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}
